import { apiClient } from "../lib/apiClient";
import logger from "../utils/logger";
import {
  OnboardingProgress,
  AssessmentQuestion,
  PracticeQuestion,
  AssessmentAnswer,
  SkillAssessment,
  VocabularyItem,
  LanguageLevel,
  calculateAssessmentLevel,
} from "../types/onboarding";

export class OnboardingService {
  /**
   * Get user's onboarding progress
   */
  static async getOnboardingProgress(
    _userId: string,
  ): Promise<OnboardingProgress | null> {
    try {
      const data = await apiClient.getOnboardingProgress();
      return data;
    } catch (error) {
      logger.error("Failed to get onboarding progress:", error);
      throw error;
    }
  }

  /**
   * Update onboarding progress
   */
  static async updateOnboardingProgress(
    userId: string,
    step: number,
    data: Partial<OnboardingProgress>,
  ): Promise<void> {
    try {
      const progressData = {
        current_step: step,
        completed_steps: data.completed_steps,
        assessment_results: data.assessment_results,
        language_goals: data.language_goals,
        selected_vocabulary: data.selected_vocabulary,
      };

      await apiClient.updateOnboardingProgress(progressData);
      logger.info(
        `Updated onboarding progress for user ${userId} to step ${step}`,
      );
    } catch (error) {
      logger.error("Failed to update onboarding progress:", error);
      throw error;
    }
  }

  /**
   * Mark onboarding as completed
   */
  static async completeOnboarding(userId: string): Promise<void> {
    try {
      await apiClient.updateOnboardingProgress({
        completed_at: new Date().toISOString(),
        current_step: 7, // Beyond the last step
      });
      logger.info(`Completed onboarding for user ${userId}`);
    } catch (error) {
      logger.error("Failed to complete onboarding:", error);
      throw error;
    }
  }

  /**
   * Initialize onboarding for a user
   */
  static async initializeOnboarding(
    userId: string,
  ): Promise<OnboardingProgress> {
    try {
      // The backend creates default progress if it doesn't exist when we fetch it
      const progress = await this.getOnboardingProgress(userId);
      if (!progress) {
        // This shouldn't happen given the backend logic, but as a fallback:
        const defaultProgress = {
          current_step: 1,
          completed_steps: [],
          selected_vocabulary: [],
        };
        await this.updateOnboardingProgress(userId, 1, defaultProgress);
        return (await this.getOnboardingProgress(userId)) as OnboardingProgress;
      }
      return progress;
    } catch (error) {
      logger.error("Failed to initialize onboarding:", error);
      throw error;
    }
  }

  /**
   * Check if user has completed onboarding
   */
  static async hasCompletedOnboarding(userId: string): Promise<boolean> {
    try {
      const progress = await this.getOnboardingProgress(userId);
      // If progress exists and has a completed_at date, return true
      return !!(progress && progress.completed_at);
    } catch (error) {
      logger.error("Failed to check onboarding status:", error);
      return false;
    }
  }

  /**
   * Get assessment questions using dynamic AI generation
   */
  static async getAssessmentQuestions(
    language: string,
    nativeLanguage: string = "english",
    minDifficulty: number = 1,
    maxDifficulty: number = 5,
    limit: number = 20,
  ): Promise<AssessmentQuestion[]> {
    try {
      const questions: AssessmentQuestion[] = [];

      // Generate questions for each difficulty level in the range
      for (
        let difficulty = minDifficulty;
        difficulty <= maxDifficulty;
        difficulty++
      ) {
        const questionsPerLevel = Math.ceil(
          limit / (maxDifficulty - minDifficulty + 1),
        );

        try {
          const response = await apiClient.generateAssessmentQuestions({
            language: language.toLowerCase(),
            nativeLanguage: nativeLanguage.toLowerCase(),
            difficultyLevel: difficulty,
            count: questionsPerLevel,
            questionTypes: ["multiple_choice", "fill_blank", "translation"],
          });

          if (response.questions && Array.isArray(response.questions)) {
            questions.push(...response.questions);
          }
        } catch (error) {
          logger.error(
            `Error generating questions for difficulty ${difficulty}:`,
            error,
          );
          continue;
        }
      }

      // If no questions were generated, use fallback static questions
      if (questions.length === 0) {
        logger.warn(
          "No AI-generated questions available, using fallback questions",
        );
        return this.getFallbackQuestions(language, nativeLanguage, limit);
      }

      // Shuffle questions to randomize order and limit to requested count
      const shuffledQuestions = this.shuffleArray(questions);
      return shuffledQuestions.slice(0, limit);
    } catch (error) {
      logger.error("Failed to get assessment questions:", error);
      // Return fallback questions on error
      return this.getFallbackQuestions(language, nativeLanguage, limit);
    }
  }

  /**
   * Get adaptive assessment questions based on user performance
   */
  static async getAdaptiveAssessmentQuestions(
    language: string,
    nativeLanguage: string = "english",
    currentLevel: number = 1,
    previousAnswers: AssessmentAnswer[] = [],
  ): Promise<AssessmentQuestion[]> {
    try {
      // Calculate performance on recent questions
      const recentCorrect = previousAnswers
        .slice(-5)
        .filter((a) => a.is_correct).length;
      const recentTotal = Math.min(previousAnswers.length, 5);

      let targetDifficulty = currentLevel;

      if (recentTotal >= 3) {
        const recentAccuracy = recentCorrect / recentTotal;
        if (recentAccuracy >= 0.8 && currentLevel < 5) {
          targetDifficulty = Math.min(currentLevel + 1, 5);
        } else if (recentAccuracy <= 0.4 && currentLevel > 1) {
          targetDifficulty = Math.max(currentLevel - 1, 1);
        }
      }

      // Get questions around the target difficulty using dynamic generation
      const minDiff = Math.max(1, targetDifficulty - 1);
      const maxDiff = Math.min(5, targetDifficulty + 1);

      return await this.getAssessmentQuestions(
        language,
        nativeLanguage,
        minDiff,
        maxDiff,
        5,
      );
    } catch (error) {
      logger.error("Failed to get adaptive assessment questions:", error);
      throw error;
    }
  }

  /**
   * Generate a single adaptive question based on previous performance
   */
  static async generateAdaptiveQuestion(
    language: string,
    nativeLanguage: string,
    previousAnswers: AssessmentAnswer[],
    nextDifficultyLevel: number,
  ): Promise<AssessmentQuestion | null> {
    try {
      // Prepare previous answers for the AI context
      const answerContext = previousAnswers.slice(-5).map((answer) => ({
        questionId: answer.question_id,
        userAnswer: answer.user_answer,
        wasCorrect: answer.is_correct,
      }));

      const response = await apiClient.generateAdaptiveQuestion({
        language: language.toLowerCase(),
        nativeLanguage: nativeLanguage.toLowerCase(),
        previousAnswers: answerContext,
        nextDifficultyLevel,
      });

      return response.question || null;
    } catch (error) {
      logger.error("Failed to generate adaptive question:", error);
      return null;
    }
  }

  /**
   * Save skill assessment results
   */
  static async saveSkillAssessment(
    userId: string,
    language: string,
    answers: AssessmentAnswer[],
    assessmentType: "initial" | "progress" | "placement" = "initial",
  ): Promise<SkillAssessment> {
    try {
      const score = answers.filter((a) => a.is_correct).length;
      const totalQuestions = answers.length;
      const levelDetermined = calculateAssessmentLevel(score, totalQuestions);

      // Analyze strengths and weaknesses
      const grammarPoints = answers
        .map((a) => a.grammar_point)
        .filter((gp): gp is string => !!gp);
      const correctGrammarPoints = answers
        .filter((a) => a.is_correct && a.grammar_point)
        .map((a) => a.grammar_point as string);
      const incorrectGrammarPoints = answers
        .filter((a) => !a.is_correct && a.grammar_point)
        .map((a) => a.grammar_point as string);

      const areasOfStrength = [...new Set(correctGrammarPoints)];
      const areasForImprovement = [...new Set(incorrectGrammarPoints)];

      // Generate recommendations
      const recommendedStartingPoint = this.generateRecommendations(
        levelDetermined,
        areasOfStrength,
        areasForImprovement,
      );

      const assessmentData = {
        language: language.toLowerCase(),
        assessment_type: assessmentType,
        level_determined: levelDetermined,
        score,
        total_questions: totalQuestions,
        areas_of_strength: areasOfStrength,
        areas_for_improvement: areasForImprovement,
        recommended_starting_point: recommendedStartingPoint,
        question_responses: answers,
      };

      const data = await apiClient.saveSkillAssessment(assessmentData);

      logger.info(
        `Saved skill assessment for user ${userId}: ${levelDetermined}`,
      );
      return data;
    } catch (error) {
      logger.error("Failed to save skill assessment:", error);
      throw error;
    }
  }

  /**
   * Get user's skill assessments
   */
  static async getUserSkillAssessments(
    userId: string,
  ): Promise<SkillAssessment[]> {
    try {
      const data = await apiClient.getSkillAssessments();
      return data || [];
    } catch (error) {
      logger.error("Failed to get skill assessments:", error);
      throw error;
    }
  }

  /**
   * Get vocabulary items for initial selection
   */
  static async getVocabularyForSelection(
    language: string,
    level: LanguageLevel = "beginner_low",
    limit: number = 100,
  ): Promise<VocabularyItem[]> {
    try {
      // This would typically come from a vocabulary database
      // For now, we'll return a mock set based on common words
      const vocabularyItems = this.getMockVocabulary(language, level, limit);
      return vocabularyItems;
    } catch (error) {
      logger.error("Failed to get vocabulary for selection:", error);
      throw error;
    }
  }

  /**
   * Save user's vocabulary selections
   */
  static async saveVocabularySelections(
    userId: string,
    selectedWords: string[],
    language: string,
    nativeLanguage: string = "english",
  ): Promise<void> {
    try {
      // Update onboarding progress with selected vocabulary
      const progress = await this.getOnboardingProgress(userId);
      if (progress) {
        await this.updateOnboardingProgress(userId, progress.current_step, {
          ...progress,
          selected_vocabulary: selectedWords,
        });
      }

      if (selectedWords.length > 0) {
        logger.info(
          `Processing ${selectedWords.length} vocabulary selections for user ${userId}`,
        );

        const savedWords: string[] = [];

        // Save words
        for (const word of selectedWords) {
          try {
            await apiClient.createWord(word, language.toLowerCase());
            savedWords.push(word);
          } catch (error) {
            logger.error(`Error saving word "${word}":`, error);
          }
        }

        // Generate sentences for all saved words
        if (savedWords.length > 0) {
          logger.info(
            `Generating sentences for ${savedWords.length} vocabulary words`,
          );

          // Process words in smaller batches (max 5 words per batch)
          const batchSize = 5;
          for (let i = 0; i < savedWords.length; i += batchSize) {
            const batch = savedWords.slice(i, i + batchSize);

            // Generate for each word in the batch (since we don't have batch generation yet)
            for (const word of batch) {
              try {
                await apiClient.generateSentences({
                  word,
                  language: language.toLowerCase(),
                  limit: 3,
                  nativeLanguage: nativeLanguage.toLowerCase(),
                });
              } catch (error) {
                logger.error(
                  `Error generating sentences for word "${word}":`,
                  error,
                );
              }
              // Short delay
              await new Promise((resolve) => setTimeout(resolve, 500));
            }

            // Short delay between batches
            if (i + batchSize < savedWords.length) {
              await new Promise((resolve) => setTimeout(resolve, 1500));
            }
          }

          logger.info(
            `Completed sentence generation for all ${savedWords.length} vocabulary words`,
          );
        }
      }

      logger.info(
        `Saved ${selectedWords.length} vocabulary selections for user ${userId}`,
      );
    } catch (error) {
      logger.error("Failed to save vocabulary selections:", error);
      throw error;
    }
  }

  /**
   * Get guided practice questions for onboarding
   */
  static async getGuidedPracticeQuestions(
    language: string,
    vocabulary: string[],
    count: number = 3,
    nativeLanguage: string = "english",
  ): Promise<PracticeQuestion[]> {
    try {
      // Generate simple practice questions using the selected vocabulary
      const practiceQuestions: PracticeQuestion[] = [];

      // Use first few vocabulary words for simple translation questions
      const wordsToUse = vocabulary.slice(0, count);

      for (let i = 0; i < Math.min(count, wordsToUse.length); i++) {
        const word = wordsToUse[i];
        practiceQuestions.push({
          id: `practice_${i + 1}`,
          type: "translation",
          question: `How do you say "${word}" in ${this.getLanguageDisplayName(nativeLanguage)}?`,
          correct_answer: word,
          explanation: `"${word}" is a common word you selected during vocabulary building.`,
          hint: `Think about the words you chose earlier.`,
          difficulty: 1,
        });
      }

      // If we don't have enough vocabulary, add some basic questions
      while (practiceQuestions.length < count) {
        const basicQuestions: PracticeQuestion[] = [
          {
            id: `basic_${practiceQuestions.length + 1}`,
            type: "multiple_choice",
            question: 'What does "Hello" mean?',
            options: ["Goodbye", "Hello", "Thank you", "Please"],
            correct_answer: "Hello",
            explanation:
              "Hello is a common greeting used when meeting someone.",
            hint: "This is a basic greeting.",
            difficulty: 1,
          },
          {
            id: `basic_${practiceQuestions.length + 1}`,
            type: "multiple_choice",
            question: 'What does "Thank you" mean?',
            options: ["Sorry", "Please", "Thank you", "Yes"],
            correct_answer: "Thank you",
            explanation: "Thank you is used to express gratitude.",
            hint: "You say this when someone helps you.",
            difficulty: 1,
          },
          {
            id: `basic_${practiceQuestions.length + 1}`,
            type: "translation",
            question: `How do you say "Yes" in ${language}?`,
            correct_answer: "Yes", // This should ideally be translated
            explanation: "A basic affirmative word.",
            hint: "Opposite of No.",
            difficulty: 1,
          },
        ];

        // Add a random basic question that isn't already added
        const nextQ =
          basicQuestions[practiceQuestions.length % basicQuestions.length];
        // Ensure unique ID
        nextQ.id = `basic_${practiceQuestions.length + 1}`;
        practiceQuestions.push(nextQ);
      }

      return practiceQuestions;
    } catch (error) {
      logger.error("Failed to get guided practice questions:", error);
      return [];
    }
  }

  // Helper methods
  private static shuffleArray<T>(array: T[]): T[] {
    const newArray = [...array];
    for (let i = newArray.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [newArray[i], newArray[j]] = [newArray[j], newArray[i]];
    }
    return newArray;
  }

  private static generateRecommendations(
    level: LanguageLevel,
    strengths: string[],
    weaknesses: string[],
  ): string {
    // Simple recommendation logic
    const levelMap: Record<string, string> = {
      beginner_low: "Start with basic vocabulary and simple greetings.",
      beginner_high:
        "Focus on constructing simple sentences and basic grammar.",
      intermediate_low:
        "Practice past and future tenses and expand vocabulary.",
      intermediate_high:
        "Work on complex sentence structures and reading comprehension.",
      advanced_low: "Engage with native content and nuanced expression.",
      advanced_high: "Refine fluency and idiomatic usage.",
    };

    let recommendation = levelMap[level] || levelMap["beginner_low"];

    if (weaknesses.length > 0) {
      recommendation += ` Pay special attention to: ${weaknesses.slice(0, 3).join(", ")}.`;
    }

    return recommendation;
  }

  private static getLanguageDisplayName(code: string): string {
    const languages: Record<string, string> = {
      en: "English",
      es: "Spanish",
      fr: "French",
      de: "German",
      it: "Italian",
      pt: "Portuguese",
      ru: "Russian",
      zh: "Chinese",
      ja: "Japanese",
      ko: "Korean",
      nl: "Dutch",
      he: "Hebrew",
    };
    return languages[code.toLowerCase()] || code;
  }

  private static getMockVocabulary(
    language: string,
    level: string,
    limit: number,
  ): VocabularyItem[] {
    // Mock vocabulary data
    const commonWords = [
      "Hello",
      "Goodbye",
      "Please",
      "Thank you",
      "Yes",
      "No",
      "Water",
      "Food",
      "House",
      "Car",
      "Book",
      "Phone",
      "Friend",
      "Family",
      "Love",
      "Happy",
      "Sad",
      "Good",
      "Bad",
      "Big",
      "Small",
      "Hot",
      "Cold",
      "Today",
      "Tomorrow",
      "Yesterday",
      "Work",
      "School",
      "Time",
      "Money",
    ];

    return commonWords.slice(0, limit).map((word, index) => ({
      id: `vocab-${index}`,
      word: word, // In a real app, this would be translated
      translation: word,
      category: "Common",
      difficulty: 1,
      frequency_rank: index + 1,
      known: false,
      image_url: `https://source.unsplash.com/random?${word}`,
    }));
  }

  private static getFallbackQuestions(
    language: string,
    nativeLanguage: string,
    limit: number,
  ): AssessmentQuestion[] {
    const fallbackQuestions: AssessmentQuestion[] = [
      {
        id: "fb-1",
        language: language.toLowerCase(),
        question_type: "multiple_choice",
        question_text: "Which is a greeting?",
        options: ["Apple", "Hello", "Run", "Blue"],
        correct_answer: "Hello",
        explanation: "Hello is a standard greeting.",
        grammar_point: "Greetings",
        difficulty_level: 1,
        created_at: new Date().toISOString(),
      },
      {
        id: "fb-2",
        language: language.toLowerCase(),
        question_type: "translation",
        question_text: `Translate "Thank you" to ${language}`,
        correct_answer: "Thank you", // Mock
        explanation: "Expression of gratitude.",
        grammar_point: "Polite expressions",
        difficulty_level: 1,
        created_at: new Date().toISOString(),
      },
      {
        id: "fb-3",
        language: language.toLowerCase(),
        question_type: "fill_blank",
        question_text: "I ___ a student.",
        options: ["am", "is", "are", "be"],
        correct_answer: "am",
        explanation: "First person singular of to be.",
        grammar_point: 'Verb "to be"',
        difficulty_level: 1,
        created_at: new Date().toISOString(),
      },
    ];

    // Repeat to fill limit
    const result: AssessmentQuestion[] = [];
    while (result.length < limit) {
      const q: AssessmentQuestion =
        fallbackQuestions[result.length % fallbackQuestions.length];
      result.push({ ...q, id: `fb-${result.length}` });
    }

    return result;
  }
}
